import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Check } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@scio3/ui';
import { cn } from '@/lib/utils';
import { SORT_DIRECTION } from '@scio3/shared-types';
import type { SortDirection } from '@scio3/shared-types';

export interface SortOption<TField extends string = string> {
  field: TField;
  direction: SortDirection;
  labelKey: string;
}

export interface SortDropdownSection {
  label: string;
  options: Array<{ value: string; labelKey: string }>;
  value: string;
  onChange: (value: string) => void;
}

/**
 * Predefined sort options matching the standard set used across the app:
 * Update Date, Created Date, Name — each with ascending/descending.
 */
export const COMMON_SORT_OPTIONS: SortOption[] = [
  { field: 'lastUpdatedDate', direction: SORT_DIRECTION.DESC, labelKey: 'Update Date (Newest)' },
  { field: 'lastUpdatedDate', direction: SORT_DIRECTION.ASC, labelKey: 'Update Date (Oldest)' },
  { field: 'createdAt', direction: SORT_DIRECTION.DESC, labelKey: 'Created Date (Newest)' },
  { field: 'createdAt', direction: SORT_DIRECTION.ASC, labelKey: 'Created Date (Oldest)' },
  { field: 'name', direction: SORT_DIRECTION.ASC, labelKey: 'Name (A–Z)' },
  { field: 'name', direction: SORT_DIRECTION.DESC, labelKey: 'Name (Z–A)' },
];

const sectionLabelClass = 'text-xs uppercase tracking-wide text-muted-foreground font-medium';

interface SortDropdownProps<TField extends string = string> {
  options: SortOption<TField>[];
  sortField: TField;
  sortDirection: SortDirection;
  onSort: (field: TField, direction: SortDirection) => void;
  align?: 'start' | 'end';
  variant?: 'outline' | 'ghost';
  sections?: SortDropdownSection[];
}

export function SortDropdown<TField extends string = string>({
  options,
  sortField,
  sortDirection,
  onSort,
  align = 'end',
  variant = 'outline',
  sections,
}: SortDropdownProps<TField>) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size="icon-sm"
          className="h-8 w-8 shrink-0"
          aria-label={t('Sort')}
        >
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuLabel className={sectionLabelClass}>{t('Sort by')}</DropdownMenuLabel>
        {options.map((option) => {
          const isActive = option.field === sortField && option.direction === sortDirection;
          return (
            <DropdownMenuItem
              key={`${option.field}-${option.direction}`}
              onClick={() => onSort(option.field, option.direction)}
              className={cn('pl-8', isActive && 'bg-accent font-medium')}
            >
              {isActive && <Check className="absolute left-2 h-4 w-4" />}
              {t(option.labelKey)}
            </DropdownMenuItem>
          );
        })}

        {sections?.map((section) => (
          <div key={section.label}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className={sectionLabelClass}>{t(section.label)}</DropdownMenuLabel>
            {section.options.map((opt) => {
              const isActive = section.value === opt.value;
              return (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => section.onChange(opt.value)}
                  className={cn('pl-8', isActive && 'bg-accent font-medium')}
                >
                  {isActive && <Check className="absolute left-2 h-4 w-4" />}
                  {t(opt.labelKey)}
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
